/**
 * Personal dashboard prototype (`/dashboard-personal`).
 *
 * Combines schema-driven multi-workflow widgets with tabs, scope, templates, and UX polish.
 * Dev-only (`featureFlags.personalDashboard`); no sidebar link — navigate to the URL directly.
 */

import { useState, useMemo } from 'react'
import {
  PlusIcon,
  PencilSimpleIcon,
  XIcon,
  GearIcon,
  DotsSixVerticalIcon,
  FloppyDiskIcon,
  SlidersIcon,
} from '@phosphor-icons/react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/hooks/use-toast'
import { ScopeEditorFull } from './components/scope-editor-full'
import { WidgetConfigPanel } from './components/widget-config-panel'
import { OpenActionQueueWidget } from './widgets/open-action-queue-widget'
import { InsightsWidget } from './widgets/insights-widget'
import { ImpactSummaryWidget } from './widgets/impact-summary-widget'
// Excursion-style widgets — local copies under this folder
import { SeverityDistributionWidget } from './widgets/severity-distribution-widget'
import { ImpactByProductWidget } from './widgets/impact-by-product-widget'
import { ExcursionTableWidget } from './widgets/excursion-table-widget'
import {
  getMockExcursions,
  getSeverityDistribution,
  getImpactByProduct,
} from './mock-excursion-data'
import { getMultiWorkflowInsights, filterInsightsByScope } from './mock-multi-workflow-data'
import { generateScopeLabel } from './utils/scope-label'
import type {
  PersonalDashboard,
  DashboardWidget,
  DashboardScope,
  WidgetType,
  PersonaTemplate,
} from './types'

// Widget catalog
const WIDGET_CATALOG = [
  // Dashboard 4 widgets (exact from screenshot)
  {
    type: 'severity_distribution' as WidgetType,
    name: 'Severity Distribution',
    description: 'Donut chart showing excursion severity breakdown',
    category: 'Overview',
    widgetDisplayType: 'Chart' as const,
  },
  {
    type: 'impact_by_product' as WidgetType,
    name: 'Impact by Product',
    description: 'Vertical bar chart showing lots and wafers by product',
    category: 'Overview',
    widgetDisplayType: 'Chart' as const,
  },
  {
    type: 'excursion_table' as WidgetType,
    name: 'Excursion Details',
    description: 'Searchable table with all excursion data',
    category: 'Details',
    widgetDisplayType: 'Table' as const,
  },
  // Dashboard 1 generic widgets
  {
    type: 'open_action_queue' as WidgetType,
    name: 'Action Queue',
    description: 'Pending issues from any workflow',
    category: 'Actions',
    widgetDisplayType: 'Table' as const,
  },
  {
    type: 'insights_needing_attention' as WidgetType,
    name: 'High Priority Insights',
    description: 'Critical insights across workflows',
    category: 'Alerts',
    widgetDisplayType: 'Chart' as const,
  },
  {
    type: 'impacted_business_objects' as WidgetType,
    name: 'Business Impact',
    description: 'Aggregated impact metrics',
    category: 'Impact',
    widgetDisplayType: 'Chart' as const,
  },
]

// Templates
const PERSONA_TEMPLATES: Record<
  PersonaTemplate,
  {
    name: string
    description: string
    icon: string
    getWidgets: () => DashboardWidget[]
    defaultScope: string
  }
> = {
  product_engineer: {
    name: 'Product Engineer',
    description: 'Daily monitoring for assigned products with yield trends and actions',
    icon: '👷',
    defaultScope: 'My Products • All workflows',
    getWidgets: () => [
      {
        id: 'pe-insights',
        type: 'insights_needing_attention',
        title: 'My Products - High Priority',
        position: { row: 0, col: 0 },
        size: { width: 2, height: 2 },
        usesCustomScope: false,
        config: { showWorkflowSource: true },
      },
      {
        id: 'pe-impact',
        type: 'impacted_business_objects',
        title: 'Impact Summary',
        position: { row: 0, col: 2 },
        size: { width: 1, height: 2 },
        usesCustomScope: false,
        config: {},
      },
      {
        id: 'pe-actions',
        type: 'open_action_queue',
        title: 'My Action Queue',
        position: { row: 2, col: 0 },
        size: { width: 3, height: 2 },
        usesCustomScope: false,
        config: { showWorkflowSource: true },
      },
    ],
  },
  process_engineer: {
    name: 'Process Engineer',
    description: 'Node-level analysis with cross-product patterns',
    icon: '🔬',
    defaultScope: 'Node-scoped • Process workflows',
    getWidgets: () => [
      {
        id: 'fe-insights',
        type: 'insights_needing_attention',
        title: 'Process & WAT Signals',
        position: { row: 0, col: 0 },
        size: { width: 2, height: 2 },
        usesCustomScope: false,
        config: { showWorkflowSource: true },
      },
      {
        id: 'fe-queue',
        type: 'open_action_queue',
        title: 'Process Issues',
        position: { row: 2, col: 0 },
        size: { width: 3, height: 2 },
        usesCustomScope: false,
        config: { showWorkflowSource: true },
      },
    ],
  },
  engineering_lead: {
    name: 'Engineering Lead',
    description: 'Portfolio health with team workload',
    icon: '👔',
    defaultScope: 'Portfolio • All workflows',
    getWidgets: () => [
      {
        id: 'em-insights',
        type: 'insights_needing_attention',
        title: 'Critical Issues',
        position: { row: 0, col: 0 },
        size: { width: 2, height: 2 },
        usesCustomScope: false,
        config: { showWorkflowSource: true },
      },
      {
        id: 'em-impact',
        type: 'impacted_business_objects',
        title: 'Portfolio Impact',
        position: { row: 0, col: 2 },
        size: { width: 1, height: 2 },
        usesCustomScope: false,
        config: {},
      },
      {
        id: 'em-queue',
        type: 'open_action_queue',
        title: 'Team Action Queue',
        position: { row: 2, col: 0 },
        size: { width: 3, height: 2 },
        usesCustomScope: false,
        config: { showWorkflowSource: true },
      },
    ],
  },
  test_engineer: {
    name: 'Test Engineer',
    description: 'Test operations and efficiency',
    icon: '⚡',
    defaultScope: 'Test-scoped • All workflows',
    getWidgets: () => [
      {
        id: 'te-queue',
        type: 'open_action_queue',
        title: 'Test Issues',
        position: { row: 0, col: 0 },
        size: { width: 3, height: 2 },
        usesCustomScope: false,
        config: { showWorkflowSource: true },
      },
    ],
  },
  senior_executive: {
    name: 'Senior Executive',
    description: 'Portfolio summary - matches Executive View default',
    icon: '🎯',
    defaultScope: 'Portfolio • All workflows',
    getWidgets: () => [
      {
        id: 'exec-insights',
        type: 'insights_needing_attention',
        title: 'Critical & High Priority',
        position: { row: 0, col: 0 },
        size: { width: 1, height: 1 },
        usesCustomScope: false,
        config: { showWorkflowSource: true, limit: 6 },
      },
      {
        id: 'exec-impact',
        type: 'impacted_business_objects',
        title: 'Business Impact',
        position: { row: 0, col: 1 },
        size: { width: 2, height: 1 },
        usesCustomScope: false,
        config: {},
      },
      {
        id: 'exec-queue',
        type: 'open_action_queue',
        title: 'Action Queue',
        position: { row: 1, col: 0 },
        size: { width: 3, height: 2 },
        usesCustomScope: false,
        config: { showWorkflowSource: true },
      },
    ],
  },
}

export function DashboardPersonalPage() {
  // Default Executive View - TOP 3 from Dashboard 4, BOTTOM 3 from Dashboard 1
  const defaultDashboard: PersonalDashboard = {
    id: 'exec-default',
    name: 'Executive View',
    description: 'Portfolio overview across all workflows',
    scope: {
      dateRange: 'last_quarter',
      // No filters = Portfolio (all products, all workflows, all nodes)
    },
    widgets: [
      // ROW 1: Top 3 widgets with CUSTOM SCOPE (Yield Excursion only)
      {
        id: 'widget-severity-dist',
        type: 'severity_distribution',
        title: 'Severity Distribution',
        position: { row: 0, col: 0 },
        size: { width: 1, height: 1 },
        usesCustomScope: true, // Custom scope!
        customScope: {
          workflows: ['Yield Excursion'], // Only Yield Excursion workflow
        },
        config: {},
      },
      {
        id: 'widget-impact-product',
        type: 'impact_by_product',
        title: 'Impact by Product',
        position: { row: 0, col: 1 },
        size: { width: 2, height: 1 },
        usesCustomScope: true, // Custom scope!
        customScope: {
          workflows: ['Yield Excursion'], // Only Yield Excursion workflow
        },
        config: {},
      },
      // ROW 2: Excursion Details Table (full width) - Also custom scope
      {
        id: 'widget-exc-table',
        type: 'excursion_table',
        title: 'Insight Table (Yield Excursions)',
        position: { row: 1, col: 0 },
        size: { width: 3, height: 2 },
        usesCustomScope: true, // Custom scope!
        customScope: {
          workflows: ['Yield Excursion'], // Only Yield Excursion workflow
        },
        config: {},
      },
      // ROW 3: Bottom widgets using DASHBOARD SCOPE (All workflows)
      {
        id: 'widget-insights',
        type: 'insights_needing_attention',
        title: 'Critical & High Priority (All Workflows)',
        position: { row: 3, col: 0 },
        size: { width: 1, height: 1 },
        usesCustomScope: false, // Uses dashboard scope (all workflows)
        config: { showWorkflowSource: true, limit: 6 },
      },
      {
        id: 'widget-impact',
        type: 'impacted_business_objects',
        title: 'Business Impact (All Workflows)',
        position: { row: 3, col: 1 },
        size: { width: 2, height: 1 },
        usesCustomScope: false, // Uses dashboard scope (all workflows)
        config: {},
      },
      // ROW 4: Action Queue using DASHBOARD SCOPE (All workflows)
      {
        id: 'widget-queue',
        type: 'open_action_queue',
        title: 'Open Action Queue (All Workflows)',
        position: { row: 4, col: 0 },
        size: { width: 3, height: 2 },
        usesCustomScope: false, // Uses dashboard scope (all workflows)
        config: { showWorkflowSource: true },
      },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  const [dashboards, setDashboards] = useState<PersonalDashboard[]>([defaultDashboard])
  const [activeDashboardId, setActiveDashboardId] = useState(defaultDashboard.id)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [showNewDashboardDialog, setShowNewDashboardDialog] = useState(false)
  const [showScopeEditor, setShowScopeEditor] = useState(false)
  const [showWidgetLibrary, setShowWidgetLibrary] = useState(false)
  const [configWidgetId, setConfigWidgetId] = useState<string | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<PersonaTemplate | null>(null)
  const [newDashboardName, setNewDashboardName] = useState('')
  const [useTemplate, setUseTemplate] = useState(true)

  const activeDashboard = dashboards.find((d) => d.id === activeDashboardId) || dashboards[0]

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    setDashboards((prev) =>
      prev.map((d) => {
        if (d.id !== activeDashboardId) return d

        const oldIndex = d.widgets.findIndex((w) => w.id === active.id)
        const newIndex = d.widgets.findIndex((w) => w.id === over.id)

        const reorderedWidgets = arrayMove(d.widgets, oldIndex, newIndex)

        return {
          ...d,
          widgets: reorderedWidgets,
          updatedAt: new Date().toISOString(),
        }
      }),
    )
    setIsDirty(true)
  }

  // All insights from all workflows (Dashboard 1 data)
  const allInsights = useMemo(() => getMultiWorkflowInsights(), [])

  // Dashboard 4 excursion data (exact from screenshot)
  const excursionData = useMemo(() => getMockExcursions(), [])
  const severityData = useMemo(() => getSeverityDistribution(), [])
  const impactData = useMemo(() => getImpactByProduct(), [])

  // Filter insights by dashboard scope
  const filteredInsights = useMemo(() => {
    return filterInsightsByScope(allInsights, activeDashboard.scope)
  }, [allInsights, activeDashboard.scope])

  // Count workflows represented in filtered data
  const workflowCount = useMemo(() => {
    return new Set(filteredInsights.map((i) => i.workflowSource)).size
  }, [filteredInsights])

  const handleUpdateScope = (newScope: DashboardScope) => {
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === activeDashboardId
          ? {
              ...d,
              scope: newScope,
              updatedAt: new Date().toISOString(),
            }
          : d,
      ),
    )
    setShowScopeEditor(false)
    setIsDirty(true)
  }

  const handleCreateDashboard = (
    dashboardName?: string,
    templateKey?: PersonaTemplate,
    shouldUseTemplate?: boolean,
  ) => {
    const name = dashboardName || newDashboardName
    const template = templateKey || selectedTemplate
    const useTemplateValue = shouldUseTemplate !== undefined ? shouldUseTemplate : useTemplate

    if (!name.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a dashboard name',
        variant: 'destructive',
      })
      return
    }

    let widgets: DashboardWidget[] = []
    let description = ''

    if (useTemplateValue && template) {
      const templateData = PERSONA_TEMPLATES[template]
      widgets = templateData.getWidgets()
      description = templateData.description
    }

    const newDashboard: PersonalDashboard = {
      id: `dash-${Date.now()}`,
      name,
      description,
      scope: { dateRange: 'last_30d' },
      widgets,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    setDashboards((prev) => [...prev, newDashboard])
    setActiveDashboardId(newDashboard.id)
    setShowNewDashboardDialog(false)
    setNewDashboardName('')
    setSelectedTemplate(null)
    setIsDirty(true)
  }

  const handleDeleteDashboard = (dashboardId: string) => {
    if (dashboards.length === 1) {
      toast({
        title: 'Cannot delete',
        description: 'Keep at least one dashboard',
        variant: 'destructive',
      })
      return
    }
    setDashboards((prev) => prev.filter((d) => d.id !== dashboardId))
    if (activeDashboardId === dashboardId) {
      setActiveDashboardId(dashboards[0].id)
    }
    setIsDirty(true)
  }

  const handleAddWidget = (type: WidgetType) => {
    const widget = WIDGET_CATALOG.find((w) => w.type === type)
    if (!widget) return

    // Determine default size based on widget type for optimal readability
    let defaultWidth: DashboardWidget['size']['width'] = 1
    let defaultHeight: DashboardWidget['size']['height'] = 1

    // Tables need full width to show all columns
    if (type === 'excursion_table' || type === 'open_action_queue') {
      defaultWidth = 3
      defaultHeight = 2
    }
    // Bar charts need more width
    else if (type === 'impact_by_product') {
      defaultWidth = 2
      defaultHeight = 1
    }
    // Single-column widgets (donut charts, metrics)
    else {
      defaultWidth = 1
      defaultHeight = 1
    }

    // Find next available position in grid (3 columns)
    const currentWidgets = activeDashboard.widgets
    let nextRow = 0
    let nextCol = 0

    if (currentWidgets.length > 0) {
      // Sort by position to find the last widget
      const sortedWidgets = [...currentWidgets].sort((a, b) => {
        if (a.position.row !== b.position.row) return a.position.row - b.position.row
        return a.position.col - b.position.col
      })
      const lastWidget = sortedWidgets[sortedWidgets.length - 1]

      // Calculate next position based on last widget
      nextCol = lastWidget.position.col + lastWidget.size.width
      nextRow = lastWidget.position.row

      // If new widget won't fit in current row, move to next row
      if (nextCol + defaultWidth > 3 || nextCol >= 3) {
        nextRow = lastWidget.position.row + lastWidget.size.height
        nextCol = 0
      }
    }

    const newWidget: DashboardWidget = {
      id: `widget-${Date.now()}`,
      type,
      title: widget.name,
      position: { row: nextRow, col: nextCol },
      size: { width: defaultWidth, height: defaultHeight },
      usesCustomScope: false,
      config: { showWorkflowSource: true },
    }

    setDashboards((prev) =>
      prev.map((d) =>
        d.id === activeDashboardId
          ? { ...d, widgets: [...d.widgets, newWidget], updatedAt: new Date().toISOString() }
          : d,
      ),
    )
    setShowWidgetLibrary(false)
    setIsDirty(true)
  }

  const handleRemoveWidget = (widgetId: string) => {
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === activeDashboardId
          ? {
              ...d,
              widgets: d.widgets.filter((w) => w.id !== widgetId),
              updatedAt: new Date().toISOString(),
            }
          : d,
      ),
    )
    setIsDirty(true)
  }

  const handleSave = () => {
    setIsDirty(false)
    setIsEditMode(false)
    setConfigWidgetId(null)
    toast({ title: 'Dashboard saved' })
  }

  const handleCancelEdit = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to cancel?')
      if (!confirmed) return
      toast({ title: 'Changes discarded', variant: 'destructive' })
    }
    setIsEditMode(false)
    setConfigWidgetId(null)
    setIsDirty(false)
  }

  const handleWidgetConfigChange = (widgetId: string, key: string, value: any) => {
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === activeDashboardId
          ? {
              ...d,
              widgets: d.widgets.map((w) =>
                w.id === widgetId ? { ...w, config: { ...w.config, [key]: value } } : w,
              ),
              updatedAt: new Date().toISOString(),
            }
          : d,
      ),
    )
    setIsDirty(true)
  }

  const handleToggleCustomScope = (widgetId: string) => {
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === activeDashboardId
          ? {
              ...d,
              widgets: d.widgets.map((w) =>
                w.id === widgetId
                  ? {
                      ...w,
                      usesCustomScope: !w.usesCustomScope,
                      customScope: !w.usesCustomScope
                        ? { workflows: ['Yield Excursion'] }
                        : undefined,
                    }
                  : w,
              ),
              updatedAt: new Date().toISOString(),
            }
          : d,
      ),
    )
    setIsDirty(true)
  }

  const handleCustomScopeChange = (widgetId: string, newCustomScope: Partial<DashboardScope>) => {
    setDashboards((prev) =>
      prev.map((d) =>
        d.id === activeDashboardId
          ? {
              ...d,
              widgets: d.widgets.map((w) =>
                w.id === widgetId ? { ...w, customScope: newCustomScope } : w,
              ),
              updatedAt: new Date().toISOString(),
            }
          : d,
      ),
    )
    setIsDirty(true)
  }

  // Sortable widget component
  function SortableWidget({ widget }: { widget: DashboardWidget }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: widget.id,
      disabled: !isEditMode,
    })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      cursor: isEditMode ? 'default' : 'auto',
    }

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`space-y-3 ${widget.size.width === 3 ? 'col-span-3' : widget.size.width === 2 ? 'col-span-2' : 'col-span-1'}`}
      >
        <div className='relative rounded-lg shadow-sm'>
          {/* Edit Mode Controls */}
          {isEditMode && (
            <div className='absolute right-2 top-2 z-20 flex gap-2'>
              {/* Drag Handle */}
              <Button
                size='sm'
                variant='outline'
                className='h-8 w-8 cursor-grab rounded-lg border-[#e6e6e6] bg-white p-0 shadow-sm hover:bg-[#fafafa] active:cursor-grabbing'
                {...listeners}
                {...attributes}
                aria-label='Drag to reorder widget'
              >
                <DotsSixVerticalIcon className='h-4 w-4 text-[#727272]' />
              </Button>
              <Button
                size='sm'
                variant='outline'
                className='h-8 w-8 rounded-lg border-[#e6e6e6] bg-white p-0 shadow-sm hover:bg-[#fafafa]'
                onClick={() => setConfigWidgetId(configWidgetId === widget.id ? null : widget.id)}
                title='Configure widget'
              >
                <GearIcon className='h-4 w-4 text-[#727272]' />
              </Button>
              <Button
                size='sm'
                variant='outline'
                className='h-8 w-8 rounded-lg border-[#e6e6e6] bg-white p-0 shadow-sm hover:bg-[#fee7e7]'
                onClick={() => handleRemoveWidget(widget.id)}
              >
                <XIcon className='h-4 w-4 text-[#b41e2e]' />
              </Button>
            </div>
          )}

          {renderWidget(widget)}
        </div>

        {/* Config Panel (Below Widget) */}
        {configWidgetId === widget.id && (
          <WidgetConfigPanel
            widgetType={widget.type}
            widgetTitle={widget.title}
            config={widget.config}
            usesCustomScope={widget.usesCustomScope}
            customScope={widget.customScope}
            onConfigChange={(key, value) => handleWidgetConfigChange(widget.id, key, value)}
            onToggleCustomScope={() => handleToggleCustomScope(widget.id)}
            onCustomScopeChange={(scope) => handleCustomScopeChange(widget.id, scope)}
            onClose={() => setConfigWidgetId(null)}
          />
        )}
      </div>
    )
  }

  const renderWidget = (widget: DashboardWidget) => {
    const widgetProps = {
      showWorkflowSource: widget.config.showWorkflowSource !== false,
      limit: widget.config.limit,
    }

    switch (widget.type) {
      // Dashboard 4 widgets (exact from screenshot)
      case 'severity_distribution':
        return (
          <SeverityDistributionWidget
            data={severityData}
            usesCustomScope={widget.usesCustomScope}
            isEditMode={isEditMode}
          />
        )
      case 'impact_by_product':
        return (
          <ImpactByProductWidget
            data={impactData}
            usesCustomScope={widget.usesCustomScope}
            isEditMode={isEditMode}
          />
        )
      case 'excursion_table':
        return (
          <ExcursionTableWidget
            data={excursionData}
            usesCustomScope={widget.usesCustomScope}
            isEditMode={isEditMode}
          />
        )
      // Dashboard 1 multi-workflow widgets
      case 'open_action_queue':
        return <OpenActionQueueWidget insights={filteredInsights} {...widgetProps} />
      case 'insights_needing_attention':
        return <InsightsWidget insights={filteredInsights} {...widgetProps} />
      case 'impacted_business_objects':
        return <ImpactSummaryWidget insights={filteredInsights} />
      default:
        return (
          <div className='flex h-full items-center justify-center rounded-xl border-2 border-neutral-200 bg-white p-8'>
            <div className='text-center'>
              <div className='mb-2 text-4xl'>📊</div>
              <div className='text-sm font-semibold'>{widget.title}</div>
            </div>
          </div>
        )
    }
  }

  return (
    <div className='min-h-screen bg-white'>
      <div className='mx-auto max-w-[1600px]'>
        {/* Tabs */}
        <div className='sticky top-0 z-30 flex items-stretch border-b border-[#e6e6e6] bg-white px-4 shadow-sm'>
          {dashboards.map((dashboard) => (
            <div key={dashboard.id} className='group relative flex items-center'>
              <button
                onClick={() => {
                  setActiveDashboardId(dashboard.id)
                  setIsEditMode(false)
                }}
                className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-normal tracking-[0.14px] transition-all ${
                  dashboard.id === activeDashboardId
                    ? 'border-[#272727] text-[#272727]'
                    : 'border-transparent text-[#727272] hover:text-[#272727]'
                } ${dashboard.id !== 'exec-default' ? 'pr-2' : ''}`}
              >
                {dashboard.id === activeDashboardId && (
                  <div className='h-2 w-2 rounded-full bg-[#FF5B04]' />
                )}
                {dashboard.name}
              </button>
              <button
                onClick={() => handleDeleteDashboard(dashboard.id)}
                className='ml-1 mr-2 rounded-full p-1 text-[#a3a3a3] opacity-0 transition-all hover:bg-[#fee7e7] hover:text-[#b41e2e] group-hover:opacity-100'
              >
                <XIcon className='h-3.5 w-3.5' />
              </button>
            </div>
          ))}

          {/* New Dashboard - Just Plus Icon */}
          <Dialog open={showNewDashboardDialog} onOpenChange={setShowNewDashboardDialog}>
            <DialogTrigger asChild>
              <button
                className='flex items-center justify-center border-b-2 border-transparent px-3 py-3 text-[#727272] transition-all hover:border-[#e6e6e6] hover:text-[#272727]'
                title='Create new dashboard'
              >
                <PlusIcon className='h-5 w-5' />
              </button>
            </DialogTrigger>
            <DialogContent className='max-h-[85vh] max-w-3xl overflow-y-auto rounded-2xl p-0'>
              <DialogHeader className='border-b border-[#e6e6e6] px-4 py-4'>
                <DialogTitle className='text-xl font-medium leading-[30px] tracking-[0.2px] text-[#1d1d1d]'>
                  Create New Dashboard
                </DialogTitle>
                <DialogDescription className='text-sm leading-[21px] tracking-[0.14px] text-[#727272]'>
                  Quick start with recommendations or build your own
                </DialogDescription>
              </DialogHeader>

              <div className='space-y-6 p-4'>
                {/* RECOMMENDED DASHBOARDS SECTION */}
                <div className='rounded-2xl border border-[#e6e6e6] bg-white p-4 shadow-[0px_7px_29px_0px_rgba(100,100,111,0.1)]'>
                  <div className='mb-4 flex items-center gap-2'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      fill='#727272'
                      viewBox='0 0 256 256'
                    >
                      <path d='M176,232a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h80A8,8,0,0,1,176,232Zm40-128a87.55,87.55,0,0,1-33.64,69.21A16.24,16.24,0,0,0,176,186v6a16,16,0,0,1-16,16H96a16,16,0,0,1-16-16v-6a16,16,0,0,0-6.23-12.66A87.59,87.59,0,0,1,40,104.49C39.74,56.83,78.26,17.14,125.88,16A88,88,0,0,1,216,104Zm-16,0a72,72,0,0,0-73.74-72c-39,.92-70.47,33.39-70.26,72.39a71.65,71.65,0,0,0,27.64,56.3A32,32,0,0,1,96,186v6h64v-6a32.15,32.15,0,0,1,12.47-25.35A71.65,71.65,0,0,0,200,104Zm-16.11-9.34a57.6,57.6,0,0,0-46.56-46.55,8,8,0,0,0-2.66,15.78c16.57,2.79,30.63,16.85,33.44,33.45A8,8,0,0,0,176,104a9,9,0,0,0,1.35-.11A8,8,0,0,0,183.89,94.66Z'></path>
                    </svg>
                    <div>
                      <h3 className='text-base font-medium tracking-[0.16px] text-[#1d1d1d]'>
                        Recommended for You
                      </h3>
                      <p className='text-xs tracking-[0.12px] text-[#727272]'>
                        One-click dashboard creation
                      </p>
                    </div>
                  </div>
                  <div className='grid grid-cols-3 gap-3'>
                    {[
                      {
                        name: 'My Products',
                        desc: 'Monitor your assigned products',
                        template: 'product_engineer' as PersonaTemplate,
                      },
                      {
                        name: '28nm Deep Dive',
                        desc: 'Analyze 28nm node activity',
                        template: 'process_engineer' as PersonaTemplate,
                      },
                      {
                        name: 'Critical Issues',
                        desc: 'Focus on high-severity items',
                        template: 'engineering_lead' as PersonaTemplate,
                      },
                    ].map((rec) => (
                      <button
                        key={rec.name}
                        onClick={() => handleCreateDashboard(rec.name, rec.template, true)}
                        className='rounded-xl border border-[#e6e6e6] bg-[#fafafa] p-3 text-left transition-all hover:border-[#ff5c04] hover:bg-white hover:shadow-sm'
                      >
                        <div className='mb-1 text-sm font-medium tracking-[0.14px] text-[#1d1d1d]'>
                          {rec.name}
                        </div>
                        <div className='text-xs tracking-[0.12px] text-[#727272]'>{rec.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Separator */}
                <div className='flex items-center gap-3'>
                  <div className='h-px flex-1 bg-[#e6e6e6]' />
                  <span className='text-xs font-bold tracking-[0.12px] text-[#727272]'>
                    OR BUILD YOUR OWN
                  </span>
                  <div className='h-px flex-1 bg-[#e6e6e6]' />
                </div>

                {/* MANUAL CREATION SECTION */}
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label className='text-sm tracking-[0.14px] text-[#1d1d1d]'>
                      Dashboard Name
                    </Label>
                    <Input
                      placeholder='e.g., My Products, 28nm Analysis'
                      value={newDashboardName}
                      onChange={(e) => setNewDashboardName(e.target.value)}
                      className='h-10 rounded-xl border-[#e6e6e6] text-base tracking-[0.16px] placeholder:text-[#727272]'
                    />
                  </div>

                  <div className='space-y-3'>
                    <Label className='text-sm tracking-[0.14px] text-[#1d1d1d]'>
                      Starting Point
                    </Label>
                    <div className='grid grid-cols-2 gap-3'>
                      <button
                        onClick={() => setUseTemplate(true)}
                        className={`rounded-xl border p-4 text-center transition-all ${
                          useTemplate
                            ? 'border-[#ff5c04] bg-white shadow-sm'
                            : 'border-[#e6e6e6] bg-white hover:border-[#d3d3d3]'
                        }`}
                      >
                        <div className='mb-3 flex justify-center'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='40'
                            height='40'
                            fill='#727272'
                            viewBox='0 0 256 256'
                          >
                            <path d='M80,40a40,40,0,1,0,40,40A40,40,0,0,0,80,40Zm0,64a24,24,0,1,1,24-24A24,24,0,0,1,80,104Zm96,16a40,40,0,1,0-40-40A40,40,0,0,0,176,120Zm0-64a24,24,0,1,1-24,24A24,24,0,0,1,176,56ZM80,136a40,40,0,1,0,40,40A40,40,0,0,0,80,136Zm0,64a24,24,0,1,1,24-24A24,24,0,0,1,80,200Zm136-24a8,8,0,0,1-8,8H184v24a8,8,0,0,1-16,0V184H144a8,8,0,0,1,0-16h24V144a8,8,0,0,1,16,0v24h24A8,8,0,0,1,216,176Z'></path>
                          </svg>
                        </div>
                        <div className='mb-1 text-sm font-medium tracking-[0.14px] text-[#1d1d1d]'>
                          Use Template
                        </div>
                        <div className='text-xs tracking-[0.12px] text-[#727272]'>
                          Pre-configured widgets
                        </div>
                      </button>
                      <button
                        onClick={() => setUseTemplate(false)}
                        className={`rounded-xl border p-4 text-center transition-all ${
                          !useTemplate
                            ? 'border-[#ff5c04] bg-white shadow-sm'
                            : 'border-[#e6e6e6] bg-white hover:border-[#d3d3d3]'
                        }`}
                      >
                        <div className='mb-3 flex justify-center'>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='40'
                            height='40'
                            fill='#727272'
                            viewBox='0 0 256 256'
                          >
                            <path d='M197.58,129.06,146,110l-19-51.62a15.92,15.92,0,0,0-29.88,0L78,110l-51.62,19a15.92,15.92,0,0,0,0,29.88L78,178l19,51.62a15.92,15.92,0,0,0,29.88,0L146,178l51.62-19a15.92,15.92,0,0,0,0-29.88ZM137,164.22a8,8,0,0,0-4.74,4.74L112,223.85,91.78,169A8,8,0,0,0,87,164.22L32.15,144,87,123.78A8,8,0,0,0,91.78,119L112,64.15,132.22,119a8,8,0,0,0,4.74,4.74L191.85,144ZM144,40a8,8,0,0,1,8-8h16V16a8,8,0,0,1,16,0V32h16a8,8,0,0,1,0,16H184V64a8,8,0,0,1-16,0V48H152A8,8,0,0,1,144,40ZM248,88a8,8,0,0,1-8,8h-8v8a8,8,0,0,1-16,0V96h-8a8,8,0,0,1,0-16h8V72a8,8,0,0,1,16,0v8h8A8,8,0,0,1,248,88Z'></path>
                          </svg>
                        </div>
                        <div className='mb-1 text-sm font-medium tracking-[0.14px] text-[#1d1d1d]'>
                          Start from Scratch
                        </div>
                        <div className='text-xs tracking-[0.12px] text-[#727272]'>
                          Custom layout
                        </div>
                      </button>
                    </div>
                  </div>

                  {useTemplate && (
                    <div className='space-y-3'>
                      <Label className='text-sm tracking-[0.14px] text-[#1d1d1d]'>
                        Choose Template
                      </Label>
                      <div className='grid grid-cols-2 gap-3'>
                        {Object.entries(PERSONA_TEMPLATES).map(([key, template]) => (
                          <button
                            key={key}
                            onClick={() => setSelectedTemplate(key as PersonaTemplate)}
                            className={`rounded-xl border p-4 text-left transition-all ${
                              selectedTemplate === key
                                ? 'border-[#ff5c04] bg-white shadow-sm'
                                : 'border-[#e6e6e6] bg-white hover:border-[#d3d3d3]'
                            }`}
                          >
                            <div className='mb-2 text-2xl'>{template.icon}</div>
                            <div className='mb-1 text-sm font-medium tracking-[0.14px] text-[#1d1d1d]'>
                              {template.name}
                            </div>
                            <div className='mb-2 text-xs tracking-[0.12px] text-[#727272]'>
                              {template.description}
                            </div>
                            <div className='flex flex-wrap items-center gap-2'>
                              <div className='rounded-3xl bg-[#f5f5f5] px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#272727]'>
                                {template.getWidgets().length} widgets
                              </div>
                              <span className='text-[10px] tracking-[0.1px] text-[#727272]'>
                                {template.defaultScope}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className='flex justify-end gap-3 border-t border-[#e6e6e6] pt-4'>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setShowNewDashboardDialog(false)
                      setNewDashboardName('')
                      setSelectedTemplate(null)
                    }}
                    className='h-10 rounded-lg border-0 bg-[#f5f5f5] px-4 text-sm font-bold tracking-[0.14px] text-[#272727] hover:bg-[#e6e6e6]'
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleCreateDashboard()}
                    disabled={!newDashboardName.trim() || (useTemplate && !selectedTemplate)}
                    className='h-10 rounded-lg bg-[#1d1d1d] px-4 text-sm font-bold tracking-[0.14px] text-[#fafafa] hover:bg-[#272727]'
                  >
                    <PlusIcon className='mr-2 h-4 w-4' />
                    Create Dashboard
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Header with Scope */}
        <div className='sticky top-[52px] z-20 border-b border-[#e6e6e6] bg-white shadow-sm'>
          <div className='flex items-center justify-between px-6 py-3'>
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-2 rounded-3xl bg-[#ffead5] px-2 py-1'>
                <SlidersIcon className='h-3 w-3 text-[#c83408]' />
                <span className='text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#c83408]'>
                  {generateScopeLabel(activeDashboard.scope)}
                </span>
              </div>
              <div className='flex items-center gap-2 rounded-3xl bg-[#cef2ff] px-2 py-1'>
                <span className='text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#0065b3]'>
                  {filteredInsights.length} insights • {workflowCount} workflow
                  {workflowCount !== 1 ? 's' : ''}
                </span>
              </div>
              <Dialog open={showScopeEditor} onOpenChange={setShowScopeEditor}>
                <DialogTrigger asChild>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-8 rounded-lg px-3 text-xs font-bold tracking-[0.12px] text-[#c83408] hover:bg-transparent hover:text-[#f04905]'
                  >
                    <GearIcon className='mr-1 h-3 w-3' />
                    Edit Scope
                  </Button>
                </DialogTrigger>
                <DialogContent className='max-h-[90vh] max-w-4xl overflow-y-auto'>
                  <DialogHeader>
                    <DialogTitle>Configure Dashboard Scope</DialogTitle>
                    <DialogDescription>
                      Filter by products, nodes, workflows, and more
                    </DialogDescription>
                  </DialogHeader>
                  <ScopeEditorFull
                    currentScope={activeDashboard.scope}
                    onSave={handleUpdateScope}
                    onCancel={() => setShowScopeEditor(false)}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <div className='flex gap-2'>
              {!isEditMode ? (
                <Button
                  size='sm'
                  onClick={() => setIsEditMode(true)}
                  className='h-8 rounded-lg border border-[#e6e6e6] bg-white px-3 text-xs font-bold tracking-[0.12px] text-[#272727] hover:bg-[#fafafa]'
                >
                  <PencilSimpleIcon className='mr-2 h-4 w-4' />
                  Edit Layout
                </Button>
              ) : (
                <>
                  <Dialog open={showWidgetLibrary} onOpenChange={setShowWidgetLibrary}>
                    <DialogTrigger asChild>
                      <Button
                        variant='outline'
                        size='sm'
                        className='h-8 rounded-lg border-[#e6e6e6] px-3 text-xs font-bold tracking-[0.12px] text-[#272727] hover:bg-[#fafafa]'
                      >
                        <PlusIcon className='mr-2 h-4 w-4' />
                        Add Widget
                      </Button>
                    </DialogTrigger>
                    <DialogContent className='max-w-2xl rounded-2xl p-0'>
                      <DialogHeader className='border-b border-[#e6e6e6] px-4 py-4'>
                        <DialogTitle className='text-xl font-medium leading-[30px] tracking-[0.2px] text-[#1d1d1d]'>
                          Widget Library
                        </DialogTitle>
                        <DialogDescription className='text-sm leading-[21px] tracking-[0.14px] text-[#727272]'>
                          Generic widgets that work for any workflow
                        </DialogDescription>
                      </DialogHeader>
                      <div className='grid grid-cols-2 gap-4 p-4'>
                        {WIDGET_CATALOG.map((widget) => {
                          const alreadyAdded = activeDashboard.widgets.some(
                            (w) => w.type === widget.type,
                          )
                          const iconColor = alreadyAdded ? '#a3a3a3' : '#727272'

                          // Widget icon based on type
                          const widgetIcon =
                            widget.widgetDisplayType === 'Chart' ? (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='48'
                                height='48'
                                fill={iconColor}
                                viewBox='0 0 256 256'
                              >
                                <path d='M224,200h-8V40a8,8,0,0,0-8-8H152a8,8,0,0,0-8,8V80H96a8,8,0,0,0-8,8v40H48a8,8,0,0,0-8,8v64H32a8,8,0,0,0,0,16H224a8,8,0,0,0,0-16ZM160,48h40V200H160ZM104,96h40V200H104ZM56,144H88v56H56Z'></path>
                              </svg>
                            ) : (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='48'
                                height='48'
                                fill={iconColor}
                                viewBox='0 0 256 256'
                              >
                                <path d='M224,48H32a8,8,0,0,0-8,8V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A8,8,0,0,0,224,48ZM40,112H80v32H40Zm56,0H216v32H96ZM216,64V96H40V64ZM40,160H80v32H40Zm176,32H96V160H216v32Z'></path>
                              </svg>
                            )

                          return (
                            <button
                              key={widget.type}
                              onClick={() => handleAddWidget(widget.type)}
                              disabled={alreadyAdded}
                              className={`rounded-xl border border-[#e6e6e6] p-4 text-left transition-all ${
                                alreadyAdded
                                  ? 'cursor-not-allowed bg-[#fafafa] opacity-60'
                                  : 'bg-white hover:border-[#ff5c04] hover:shadow-sm'
                              }`}
                            >
                              <div className='mb-3'>{widgetIcon}</div>
                              <div className='mb-1 text-sm font-medium tracking-[0.14px] text-[#1d1d1d]'>
                                {widget.name}
                              </div>
                              <div className='mb-2 text-xs tracking-[0.12px] text-[#727272]'>
                                {widget.description}
                              </div>
                              <div className='flex items-center gap-2'>
                                <div className='inline-flex rounded-3xl bg-[#f5f5f5] px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#272727]'>
                                  {widget.category}
                                </div>
                                <div
                                  className={`inline-flex rounded-3xl px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] ${
                                    widget.widgetDisplayType === 'Chart'
                                      ? 'bg-[#cef2ff] text-[#0065b3]'
                                      : 'bg-[#dcfde8] text-[#12833d]'
                                  }`}
                                >
                                  {widget.widgetDisplayType}
                                </div>
                              </div>
                              {alreadyAdded && (
                                <div className='mt-2 text-xs font-bold text-[#12833d]'>✓ Added</div>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Button
                    size='sm'
                    onClick={handleCancelEdit}
                    className='h-8 rounded-lg border border-[#e6e6e6] bg-white px-3 text-xs font-bold tracking-[0.12px] text-[#272727] hover:bg-[#fafafa]'
                  >
                    Cancel
                  </Button>

                  <Button
                    size='sm'
                    onClick={handleSave}
                    className='h-8 rounded-lg bg-[#1d1d1d] px-3 text-xs font-bold tracking-[0.12px] text-[#fafafa] hover:bg-[#272727]'
                  >
                    <FloppyDiskIcon className='mr-2 h-4 w-4' />
                    Save
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Widgets */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={activeDashboard.widgets.map((w) => w.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className='grid grid-cols-3 gap-4 p-6'>
              {activeDashboard.widgets.map((widget) => (
                <SortableWidget key={widget.id} widget={widget} />
              ))}

              {activeDashboard.widgets.length === 0 && (
                <div className='col-span-3 flex h-64 items-center justify-center rounded-xl border border-dashed border-[#e6e6e6]'>
                  <div className='text-center'>
                    <div className='mb-4 flex justify-center'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='64'
                        height='64'
                        fill='#727272'
                        viewBox='0 0 256 256'
                      >
                        <path d='M224,200h-8V40a8,8,0,0,0-8-8H152a8,8,0,0,0-8,8V80H96a8,8,0,0,0-8,8v40H48a8,8,0,0,0-8,8v64H32a8,8,0,0,0,0,16H224a8,8,0,0,0,0-16ZM160,48h40V200H160ZM104,96h40V200H104ZM56,144H88v56H56Z'></path>
                      </svg>
                    </div>
                    <div className='mb-4 text-xl font-medium leading-[30px] tracking-[0.2px] text-[#1d1d1d]'>
                      No widgets yet
                    </div>
                    <Button
                      onClick={() => setShowWidgetLibrary(true)}
                      className='h-10 rounded-lg bg-[#1d1d1d] px-4 text-sm font-bold tracking-[0.14px] text-[#fafafa] hover:bg-[#272727]'
                    >
                      Browse Widget Library
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  )
}
